import { call } from '@advanza/api'
import { getFlagEmoji } from '@advanza/func'
import { SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { readServiceId } from 'components/keywordMappings/keywordMappingModel'
import { useAppSelector } from 'hooks/hooks'
import { useEffect, useState } from 'react'

interface ServiceOption {
    service_id: number
    name: string
    country_code: string
}

interface KeywordMappingServiceSelectProps {
    name: string
    emptyOption: string
    value: number | null
    onChange: (serviceId: number | null) => void
    disabled?: boolean
    error?: string
}

const LOAD_ERROR = 'Could not load the services.'

let serviceOptionsRequest: Promise<ServiceOption[]> | null = null

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function readServiceOption(value: unknown): ServiceOption | null {
    if (!isRecord(value)) return null

    const serviceId = readServiceId(value.service_id)
    if (serviceId === null) return null
    if (typeof value.name !== 'string') return null
    if (typeof value.country_code !== 'string') return null

    return {
        service_id: serviceId,
        name: value.name,
        country_code: value.country_code,
    }
}

function readServiceOptions(response: unknown): ServiceOption[] {
    if (!isRecord(response) || !Array.isArray(response.items)) return []

    const options: ServiceOption[] = []
    for (const item of response.items) {
        const option = readServiceOption(item)
        if (option === null) continue
        options.push(option)
    }

    return options
}

function loadServiceOptions(): Promise<ServiceOption[]> {
    if (serviceOptionsRequest !== null) return serviceOptionsRequest

    const request = call('office/keyword-mappings/get-service-options').then((response: unknown) =>
        readServiceOptions(response),
    )
    serviceOptionsRequest = request
    request.catch(() => {
        if (serviceOptionsRequest === request) serviceOptionsRequest = null
    })

    return request
}

function compareByMarketThenName(left: ServiceOption, right: ServiceOption): number {
    if (left.country_code > right.country_code) return -1
    if (left.country_code < right.country_code) return 1
    return left.name.localeCompare(right.name)
}

function displayedError(fieldError: string | undefined, loadError: string): string | undefined {
    if (fieldError !== undefined && fieldError !== '') return fieldError
    if (loadError !== '') return loadError
    return undefined
}

const KeywordMappingServiceSelect = ({
    name,
    emptyOption,
    value,
    onChange,
    disabled,
    error,
}: KeywordMappingServiceSelectProps) => {
    const selectedCountries = useAppSelector((state) => state.countries.countries)
    const [services, setServices] = useState<ServiceOption[]>([])
    const [loadError, setLoadError] = useState('')

    useEffect(() => {
        let active = true

        void loadServiceOptions()
            .then((options) => {
                if (!active) return
                setServices(options)
                setLoadError('')
            })
            .catch(() => {
                if (!active) return
                setLoadError(LOAD_ERROR)
            })

        return () => {
            active = false
        }
    }, [])

    const options = services
        .filter((service) => selectedCountries.some((country) => country === service.country_code))
        .sort(compareByMarketThenName)
        .map((service) => ({
            name: `${service.name} ${getFlagEmoji(service.country_code)}`,
            value: service.service_id,
        }))

    return (
        <SelectSimple
            alwaysControlValue
            name={name}
            placeholder="Service"
            emptyOption={emptyOption}
            disabled={disabled}
            error={displayedError(error, loadError)}
            value={value}
            options={options}
            onChange={({ target }: SelectSimpleChangeEvent) =>
                onChange(readServiceId(target.value))
            }
        />
    )
}

export default KeywordMappingServiceSelect
